import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, CreditCard, Search, CheckCircle, XCircle, Loader2, ExternalLink, Zap, RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Card = ({ icon: Icon, title, desc, children, testId }) => (
  <div data-testid={testId} className="bg-[#0f172a] border border-white/10 rounded-2xl p-5 md:p-6 flex flex-col">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-[#D4A017]/15 border border-[#D4A017]/30 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#D4A017]" />
      </div>
      <div>
        <h3 data-testid={testId ? `${testId}-title` : undefined} className="text-white font-bold text-base">{title}</h3>
        <p data-testid={testId ? `${testId}-description` : undefined} className="text-[#94a3b8] text-xs mt-1">{desc}</p>
      </div>
    </div>
    {children}
  </div>
);

const ResultBox = ({ result }) => {
  if (!result) return null;
  const ok = result.ok === true;
  return (
    <div
      data-testid="test-panel-result"
      className={`mt-3 rounded-xl border px-3 py-2.5 text-xs leading-relaxed ${
        ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-red-500/10 border-red-500/30 text-red-200'
      }`}
    >
      <div className="flex items-center gap-2 font-semibold mb-1">
        {ok ? <CheckCircle size={14} /> : <XCircle size={14} />}
        {ok ? 'Geslaagd' : 'Mislukt'}
      </div>
      {result.message && <p>{result.message}</p>}
      {result.fout && <p className="text-red-300 mt-1">Fout: {result.fout}</p>}
      {result.duration_seconds != null && <p className="opacity-70 mt-1">Duur: {result.duration_seconds}s</p>}
      {result.overall_score != null && (
        <p className="mt-1">
          Score: <strong>{result.overall_score}/100</strong> ({result.score_label})
        </p>
      )}
      {result.top_kans && <p className="mt-1 italic opacity-80">"{result.top_kans}"</p>}
      {result.checkout_url && (
        <a
          href={result.checkout_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-2 text-[#D4A017] font-semibold hover:underline"
          data-testid="stripe-test-checkout-link"
        >
          Open Stripe checkout <ExternalLink size={12} />
        </a>
      )}
    </div>
  );
};

export default function TestPanelPage() {
  const { api } = useAuth();

  const [emailTo, setEmailTo] = useState('info@imperiaai.nl');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeResult, setStripeResult] = useState(null);

  const [scanUrl, setScanUrl] = useState('imperiaai.nl');
  const [scanCompany, setScanCompany] = useState('IMPERIA AI');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const [reengageStatus, setReengageStatus] = useState(null);
  const [reengageLoading, setReengageLoading] = useState(false);
  const [reengageResult, setReengageResult] = useState(null);

  const [demoSeedCount, setDemoSeedCount] = useState(3);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoResult, setDemoResult] = useState(null);

  const [transitionUserId, setTransitionUserId] = useState('');
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [transitionResult, setTransitionResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api('get', '/api/admin/reengage/status');
        setReengageStatus(r.data);
      } catch { /* ignore */ }
    };
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, [api]);

  const runEmail = async () => {
    if (!emailTo) return;
    setEmailLoading(true); setEmailResult(null);
    try {
      const r = await api('post', '/api/admin/test/email', { to: emailTo });
      setEmailResult(r.data);
      r.data.ok ? toast.success('Test-email verstuurd') : toast.error('Email mislukt');
    } catch (e) {
      setEmailResult({ ok: false, fout: e.response?.data?.detail || e.message });
      toast.error('Email mislukt');
    } finally { setEmailLoading(false); }
  };

  const runStripe = async () => {
    setStripeLoading(true); setStripeResult(null);
    try {
      const origin = window.location.origin;
      const r = await api('post', '/api/admin/test/stripe', { origin_url: origin });
      setStripeResult(r.data);
      r.data.ok ? toast.success('Stripe test-session aangemaakt') : toast.error('Stripe mislukt');
    } catch (e) {
      setStripeResult({ ok: false, fout: e.response?.data?.detail || e.message });
      toast.error('Stripe mislukt');
    } finally { setStripeLoading(false); }
  };

  const runScan = async () => {
    if (!scanUrl) return;
    setScanLoading(true); setScanResult(null);
    try {
      const r = await api('post', '/api/admin/test/scan', {
        website_url: scanUrl, company_name: scanCompany,
      });
      setScanResult(r.data);
      r.data.ok ? toast.success(`Scan voltooid (${r.data.overall_score}/100)`) : toast.error('Scan mislukt');
    } catch (e) {
      setScanResult({ ok: false, fout: e.response?.data?.detail || e.message });
      toast.error('Scan mislukt');
    } finally { setScanLoading(false); }
  };

  const runReengage = async () => {
    setReengageLoading(true); setReengageResult(null);
    try {
      const r = await api('post', '/api/admin/reengage/run', {});
      setReengageResult(r.data);
      if (r.data.ok && (r.data.queued || 0) > 0) toast.success(`Re-engage: ${r.data.queued} emails in de wachtrij`);
      else if (r.data.ok) toast.info('Geen kandidaten gevonden');
      else toast.error('Re-engage mislukt');
      // Refresh status
      const s = await api('get', '/api/admin/reengage/status');
      setReengageStatus(s.data);
    } catch (e) {
      setReengageResult({ ok: false, fout: e.response?.data?.detail || e.message });
      toast.error('Re-engage mislukt');
    } finally { setReengageLoading(false); }
  };

  const runDemoSeed = async () => {
    setDemoLoading(true); setDemoResult(null);
    try {
      const r = await api('post', '/api/admin/demo/seed', { count: demoSeedCount });
      setDemoResult({ ok: true, message: r.data.message, count: r.data.count });
      toast.success(`${r.data.count} demo opdrachten klaargezet`);
    } catch (e) {
      setDemoResult({ ok: false, fout: e.response?.data?.detail || e.message });
      toast.error('Demo seed mislukt');
    } finally { setDemoLoading(false); }
  };

  const runDemoClear = async () => {
    if (!window.confirm('Verwijder alle demo opdrachten van uw eigen account?')) return;
    setDemoLoading(true);
    try {
      const r = await api('delete', '/api/admin/demo/clear');
      setDemoResult({ ok: true, message: `Opgeruimd: ${r.data.opdrachten_deleted} demo opdrachten verwijderd` });
      toast.success('Demo data verwijderd');
    } catch (e) {
      setDemoResult({ ok: false, fout: e.response?.data?.detail || e.message });
      toast.error('Verwijderen mislukt');
    } finally { setDemoLoading(false); }
  };

  const runForceTransition = async () => {
    if (!transitionUserId) { toast.error('User ID vereist'); return; }
    setTransitionLoading(true); setTransitionResult(null);
    try {
      const r = await api('post', `/api/admin/billing/force-transition/${transitionUserId}`);
      setTransitionResult({ ok: true, message: `${r.data.user_id?.slice(0,8)}… → ${r.data.new_state}` });
      toast.success('Transition uitgevoerd');
    } catch (e) {
      setTransitionResult({ ok: false, fout: e.response?.data?.detail || e.message });
      toast.error('Transition mislukt');
    } finally { setTransitionLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-test-panel">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#D4A017]/15 border border-[#D4A017]/30 flex items-center justify-center">
          <Zap className="w-6 h-6 text-[#D4A017]" />
        </div>
        <div>
          <h1 className="text-white text-2xl md:text-3xl font-bold">Test Panel</h1>
          <p className="text-[#94a3b8] text-sm mt-1">
            Valideer de hele stack in 30 seconden: SMTP, Stripe betalingen, en AI scan.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        {/* SMTP test */}
        <Card testId="smtp-test-card" icon={Mail} title="SMTP — Mijndomein e-mail" desc="Controleer de ingestelde mailbox van IMPERIA AI.">
          <input
            data-testid="test-email-input"
            type="email"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            placeholder="email@bedrijf.nl"
            className="w-full bg-[#0a0f1e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#475569] focus:border-[#D4A017]/50 focus:outline-none"
          />
          <button
            data-testid="run-email-test-btn"
            onClick={runEmail}
            disabled={emailLoading || !emailTo}
            className="mt-3 w-full bg-[#D4A017] hover:bg-[#b88a13] disabled:bg-[#D4A017]/40 disabled:cursor-not-allowed text-[#0f172a] font-bold text-sm rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
          >
            {emailLoading ? <><Loader2 size={14} className="animate-spin" /> Versturen…</> : 'Test SMTP'}
          </button>
          <ResultBox result={emailResult} />
        </Card>

        {/* Stripe test */}
        <Card icon={CreditCard} title="Stripe — €0,50 testbetaling" desc="Maak een echte Stripe Checkout Session aan om de flow te valideren.">
          <div className="text-[#64748b] text-xs leading-relaxed">
            Klik op de knop om een test-session aan te maken. Je krijgt een Stripe checkout-link die in een nieuw tabblad te openen is.
          </div>
          <button
            data-testid="run-stripe-test-btn"
            onClick={runStripe}
            disabled={stripeLoading}
            className="mt-3 w-full bg-[#D4A017] hover:bg-[#b88a13] disabled:bg-[#D4A017]/40 disabled:cursor-not-allowed text-[#0f172a] font-bold text-sm rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
          >
            {stripeLoading ? <><Loader2 size={14} className="animate-spin" /> Aanmaken…</> : 'Maak Stripe test'}
          </button>
          <ResultBox result={stripeResult} />
        </Card>

        {/* AI scan test */}
        <Card icon={Search} title="AI Scan — IMPERIA AI Radar" desc="Trigger een live scan op een willekeurige website om de AI-engine te testen.">
          <input
            data-testid="test-scan-url-input"
            type="text"
            value={scanUrl}
            onChange={(e) => setScanUrl(e.target.value)}
            placeholder="bedrijf.nl"
            className="w-full bg-[#0a0f1e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#475569] focus:border-[#D4A017]/50 focus:outline-none"
          />
          <input
            data-testid="test-scan-company-input"
            type="text"
            value={scanCompany}
            onChange={(e) => setScanCompany(e.target.value)}
            placeholder="Bedrijfsnaam"
            className="mt-2 w-full bg-[#0a0f1e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#475569] focus:border-[#D4A017]/50 focus:outline-none"
          />
          <button
            data-testid="run-scan-test-btn"
            onClick={runScan}
            disabled={scanLoading || !scanUrl}
            className="mt-3 w-full bg-[#D4A017] hover:bg-[#b88a13] disabled:bg-[#D4A017]/40 disabled:cursor-not-allowed text-[#0f172a] font-bold text-sm rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
          >
            {scanLoading ? <><Loader2 size={14} className="animate-spin" /> Scannen…</> : 'Run AI scan'}
          </button>
          <ResultBox result={scanResult} />
        </Card>
      </div>

      {/* Demo Mode + Billing tools */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <Card icon={Sparkles} title="Demo Mode — seed demo opdrachten" desc="Plaats 3-5 voorbeeld 'Huidig → Beter' opdrachten op uw eigen account voor sales-demos.">
          <div className="flex gap-2 mb-3">
            {[3, 5].map(n => (
              <button
                key={n}
                data-testid={`demo-count-${n}`}
                onClick={() => setDemoSeedCount(n)}
                className={`flex-1 rounded-lg py-2 text-xs font-bold ${demoSeedCount === n ? 'bg-[#D4A017] text-[#0f172a]' : 'bg-[#0a0f1e] border border-white/10 text-[#94a3b8]'}`}
              >
                {n} opdrachten
              </button>
            ))}
          </div>
          <button
            data-testid="run-demo-seed-btn"
            onClick={runDemoSeed}
            disabled={demoLoading}
            className="w-full bg-[#D4A017] hover:bg-[#b88a13] disabled:bg-[#D4A017]/40 disabled:cursor-not-allowed text-[#0f172a] font-bold text-sm rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
          >
            {demoLoading ? <><Loader2 size={14} className="animate-spin" /> Bezig…</> : <><Sparkles size={14} /> Seed demo data</>}
          </button>
          <button
            data-testid="run-demo-clear-btn"
            onClick={runDemoClear}
            disabled={demoLoading}
            className="mt-2 w-full bg-transparent border border-white/10 hover:border-red-500/40 text-[#94a3b8] hover:text-red-300 text-xs font-semibold rounded-lg py-2 flex items-center justify-center gap-1.5"
          >
            <Trash2 size={11} /> Verwijder demo data
          </button>
          <ResultBox result={demoResult} />
        </Card>

        <Card icon={CreditCard} title="Force billing transition" desc="Forceer een launch_month → recurring overgang (slaat 30-dagen check over).">
          <input
            data-testid="transition-user-id"
            type="text"
            value={transitionUserId}
            onChange={e => setTransitionUserId(e.target.value)}
            placeholder="user_id (vraag aan klant of pak uit AI Queue)"
            className="w-full bg-[#0a0f1e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#475569] focus:border-[#D4A017]/50 focus:outline-none"
          />
          <button
            data-testid="run-force-transition-btn"
            onClick={runForceTransition}
            disabled={transitionLoading || !transitionUserId}
            className="mt-3 w-full bg-[#D4A017] hover:bg-[#b88a13] disabled:bg-[#D4A017]/40 disabled:cursor-not-allowed text-[#0f172a] font-bold text-sm rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
          >
            {transitionLoading ? <><Loader2 size={14} className="animate-spin" /> Uitvoeren…</> : 'Force naar €2.495,95/mnd'}
          </button>
          <ResultBox result={transitionResult} />
        </Card>
      </div>

      {/* Re-engage campagne */}
      <div data-testid="reengage-card" className="bg-[#0f172a] border border-white/10 rounded-2xl p-5 md:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#D4A017]/15 border border-[#D4A017]/30 flex items-center justify-center shrink-0">
            <RefreshCw className="w-5 h-5 text-[#D4A017]" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-base">Re-engage campagne — Openers → Klikkers</h3>
            <p className="text-[#94a3b8] text-xs mt-1">
              Stuurt automatisch een korte follow-up email naar leads die de eerste email <strong>geopend</strong> hebben,
              maar nog niet doorgeklikt. Wachttijd: 48 uur na de eerste open.
            </p>
            <p className="text-[#64748b] text-xs mt-1">
              Automatische schema: dagelijks om 10:00 en 16:00.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-[#0a0f1e] border border-white/10 rounded-lg p-3 text-center">
            <p className="text-[#94a3b8] text-[10px] uppercase tracking-wide font-semibold">Wachtend</p>
            <p className="text-white font-bold text-xl mt-1">{reengageStatus?.pending ?? '—'}</p>
          </div>
          <div className="bg-[#0a0f1e] border border-white/10 rounded-lg p-3 text-center">
            <p className="text-[#94a3b8] text-[10px] uppercase tracking-wide font-semibold">Verstuurd 24u</p>
            <p className="text-emerald-400 font-bold text-xl mt-1">{reengageStatus?.sent_24h ?? '—'}</p>
          </div>
          <div className="bg-[#0a0f1e] border border-white/10 rounded-lg p-3 text-center">
            <p className="text-[#94a3b8] text-[10px] uppercase tracking-wide font-semibold">Totaal</p>
            <p className="text-[#D4A017] font-bold text-xl mt-1">{reengageStatus?.sent_total ?? '—'}</p>
          </div>
        </div>
        <button
          data-testid="run-reengage-btn"
          onClick={runReengage}
          disabled={reengageLoading}
          className="w-full bg-[#D4A017] hover:bg-[#b88a13] disabled:bg-[#D4A017]/40 disabled:cursor-not-allowed text-[#0f172a] font-bold text-sm rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
        >
          {reengageLoading ? <><Loader2 size={14} className="animate-spin" /> Verzenden (max 50)…</> : 'Start re-engage nu (max 50 emails)'}
        </button>
        <ResultBox result={reengageResult && {
          ...reengageResult,
          message: reengageResult.ok
            ? (reengageResult.queued > 0
              ? `${reengageResult.queued} emails in de wachtrij — verzending verloopt in de achtergrond`
              : reengageResult.message || 'Geen kandidaten gevonden')
            : reengageResult.fout,
        }} />
      </div>

      <div className="bg-[#0f172a] border border-white/5 rounded-2xl p-5">
        <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
          <Zap size={14} className="text-[#D4A017]" /> Wat doet dit panel?
        </h3>
        <ul className="text-[#94a3b8] text-xs space-y-1.5 list-disc pl-5">
          <li><strong className="text-white">SMTP test</strong> — verstuurt 1 e-mail via de ingestelde mailbox (geen impact op de bulk worker).</li>
          <li><strong className="text-white">Stripe test</strong> — Checkout session. Geen abonnement, alleen om de payment-flow te valideren.</li>
          <li><strong className="text-white">AI scan</strong> — draait een volledige live scan op de opgegeven URL (kost EMERGENT LLM krediet).</li>
          <li><strong className="text-white">Re-engage</strong> — handmatig de openers→klikkers follow-up campagne triggeren (max 50 emails per run).</li>
        </ul>
      </div>
    </div>
  );
}
